import React from 'react'
import { TrashIcon, MinusIcon, PlusIcon } from '@heroicons/react/24/outline'
import { useCart } from '../../contexts/CartContext'
import { cn } from '../../utils/cn'
import type { Database } from '../../types/supabase'

type CartItem = Database['public']['Tables']['cart_items']['Row']

interface CartItemCardProps {
  item: CartItem & {
    booking?: Database['public']['Tables']['bookings']['Row'] | null
  }
  onRemove?: () => void
  showControls?: boolean
  className?: string
}

export function CartItemCard({ 
  item, 
  onRemove, 
  showControls = true,
  className 
}: CartItemCardProps) {
  const { updateItem, removeItem } = useCart()

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const handleQuantityChange = async (newQuantity: number) => {
    if (newQuantity < 1) return
    
    const newTotalPrice = Number(item.unit_price) * newQuantity
    await updateItem(item.id, {
      quantity: newQuantity,
      total_price: newTotalPrice.toString()
    })
  }

  const handleRemove = () => {
    if (onRemove) {
      onRemove()
    } else {
      removeItem(item.id)
    }
  }

  return (
    <div className={cn(
      "bg-[#2a2a2a] rounded-lg p-4 border border-gray-800 transition-all hover:border-gray-700",
      className
    )}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="text-sm font-medium text-white">
            {item.description}
          </h4>
          
          {/* Item Details */}
          {item.metadata && (
            <div className="mt-2 space-y-1">
              {item.metadata.booking_date && (
                <p className="text-xs text-gray-400">
                  <span className="text-gray-500">Date:</span>{' '}
                  {new Date(item.metadata.booking_date).toLocaleDateString()}
                </p>
              )}
              {item.metadata.start_time && item.metadata.end_time && (
                <p className="text-xs text-gray-400">
                  <span className="text-gray-500">Time:</span>{' '}
                  {new Date(item.metadata.start_time).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })} - {new Date(item.metadata.end_time).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              )}
              {item.metadata.facility_name && (
                <p className="text-xs text-gray-400">
                  <span className="text-gray-500">Facility:</span>{' '}
                  {item.metadata.facility_name}
                </p>
              )}
            </div>
          )}

          {/* Price and Quantity */}
          <div className="mt-3 flex items-center justify-between">
            {showControls && item.item_type !== 'custom' ? (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleQuantityChange(item.quantity - 1)}
                  className="p-1 rounded-md bg-gray-800 hover:bg-gray-700 transition-colors disabled:opacity-50"
                  disabled={item.quantity <= 1}
                >
                  <MinusIcon className="h-3 w-3 text-gray-400" />
                </button>
                <span className="text-sm text-gray-300 min-w-[2rem] text-center">
                  {item.quantity}
                </span>
                <button
                  onClick={() => handleQuantityChange(item.quantity + 1)}
                  className="p-1 rounded-md bg-gray-800 hover:bg-gray-700 transition-colors"
                >
                  <PlusIcon className="h-3 w-3 text-gray-400" />
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-400">
                Qty: {item.quantity}
              </p>
            )}
            
            <div className="text-right">
              <p className="text-xs text-gray-500">
                {formatCurrency(Number(item.unit_price))} each
              </p>
              <p className="text-sm font-medium text-gold">
                {formatCurrency(Number(item.total_price))}
              </p>
            </div>
          </div>
        </div>

        {showControls && (
          <button
            type="button"
            onClick={handleRemove}
            className="ml-4 -m-1 p-1 text-gray-400 hover:text-red-500 transition-colors"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  )
}